import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Box, Card, CardActions, CardContent, CardHeader, CardMedia, Divider, Typography } from "@mui/material";
import { RemoveButton, SelectButton, UndoButton } from "./common";
import WorkspaceImageEditor from "./WorkspaceImageEditor";

const PreviewImage = ({ image, width }) => {
  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      flexWrap: "nowrap",
    }}>
      <Box
        alt={`${width}px`}
        component="img"
        src={image}
        sx={{
          boxShadow: "0 0 3px",
          display: "inline-block",
          margin: "0 3px",
          width,
        }}
      />
      <Typography sx={{
        fontSize: "0.75em",
        lineHeight: "2em",
        textAlign: "center",
      }}>
        {width}px
      </Typography>
    </Box>
  );
};

const WorkspaceImageSelector = ({
  disabled,
  initialImage,
  onChange
}) => {
  const targetSize = 100;

  const [showEditor, setShowEditor] = useState(false);

  const [state, setState] = useState({
    currentImage: initialImage,
    undoImage: null,
  });

  const canUndo = !!state.undoImage;
  const haveImage = !!state.currentImage;

  const handleImageChange = (newImage) => {
    setState(prev => ({
      currentImage: newImage,
      undoImage: prev.currentImage,
    }));

    onChange?.(newImage);

    setShowEditor(false);
  };

  const handleRemoveClick = () => {
    setState(prev => ({
      currentImage: null,
      undoImage: prev.currentImage,
    }));

    onChange?.(null);
  };

  const handleUndoClick = () => {
    setState(prev => ({
      currentImage: prev.undoImage,
      undoImage: prev.currentImage,
    }));

    onChange?.(state.undoImage);
  };

  useEffect(() => {
    if (initialImage !== state.currentImage) {
      setState(prev => ({
        currentImage: initialImage,
        undoImage: prev.currentImage,
      }));
    }
  }, [initialImage]);

  return (
    <>
      <Card sx={{ background: "#fcfcfc" }}>
        <CardHeader
          sx={{ padding: 1 }}
          title={
            <Typography variant="h5" component="div" sx={{ fontSize: 16 }}>
              <FormattedMessage id="admin.workspaces.imageSelector.title" />
            </Typography>
          }
        />
        {haveImage &&
          <CardMedia
            sx={{
              alignItems: "flex-end",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 1,
              margin: 1,
              marginTop: 0,
              padding: 0,
            }}
          >
            <PreviewImage image={state.currentImage} width={100} />
            <Box
              sx={{
                alignItems: "inherit",
                display: "flex",
                flexDirection: "inherit",
                gap: "inherit",
              }}
            >
              <PreviewImage image={state.currentImage} width={40} />
              <PreviewImage image={state.currentImage} width={20} />
            </Box>
          </CardMedia>
        }
        {!haveImage &&
          <Box
            sx={{
              alignItems: "center",
              color: "#888",
              display: "flex",
              height: `calc(${targetSize}px + 1.5em)`,
              justifyContent: "center",
              margin: 1,
              marginTop: 0,
            }}
          >
            <em>
              <FormattedMessage id="admin.workspaces.imageSelector.imageNotSelected" />
            </em>
          </Box>
        }
        <CardContent sx={{ padding: 1, paddingTop: 0 }}>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: 14 }}>
            <FormattedMessage id="admin.workspaces.imageSelector.description" />
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: 12, mt: 1 }}>
            <em>
              <FormattedMessage id="admin.workspaces.imageSelector.clarification" />
            </em>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions
          sx={{
            flexWrap: "wrap",
            gap: 1,
            "&>:not(style)~:not(style),&>:not(:first-of-type)": {
              marginLeft: 0,
            },
            "& button, & label, & span": {
              width: "100%",
            },
          }}
        >

          <SelectButton
            disabled={disabled}
            onClick={() => setShowEditor(true)}
          />

          {haveImage && !disabled && (
            <RemoveButton
              disabled={disabled || !haveImage}
              onClick={handleRemoveClick}
            />
          )}

          {canUndo && (
            <UndoButton
              disabled={disabled}
              onClick={handleUndoClick}
            />
          )}

        </CardActions>
      </Card>

      {showEditor && (
        <WorkspaceImageEditor
          image={state.currentImage}
          onChange={handleImageChange}
          onClose={() => setShowEditor(false)}
          targetSize={targetSize}
        />
      )}
    </>
  );
};

export default WorkspaceImageSelector;
